/**
 * Favourites, collections, history and the category vocabulary.
 *
 * Grouped into one screen because they are all "the shape of the library"
 * rather than the library itself, and each is too small to earn a tab.
 */
import { useCallback, useEffect, useState } from 'react'
import Icon from '../Icon'
import { AddCategory, CategoryChips, Empty, RecipeCard } from './Bits'
import { Triage } from './Feedback'
import { Trophies } from './Game'
import type { KitchenSummary } from '../../../shared/kitchen'
import type { BuddyChoice } from '../../lib/kitchen'
import {
  CATEGORY_KINDS, addToCollection, createCategory, createCollection, deleteCategory,
  deleteCollection, history, listCollections, removeFromCollection,
  renameCategory, updateRecipe,
  type Category, type Collection, type RecipeRow, type Run,
} from '../../lib/savenmake'

type View = 'trophies' | 'favorites' | 'collections' | 'history' | 'categories' | 'feedback'

export default function Kitchen({ recipes, categories, onChanged, onOpen, onCook, canTriage, summary, buddy, onBuddy }: {
  recipes: RecipeRow[]
  categories: Category[]
  onChanged: () => void
  onOpen: (r: RecipeRow) => void
  onCook: (r: RecipeRow) => void
  /* Triage is an admin surface; the tab is absent rather than present-and-denied. */
  canTriage: boolean
  summary: KitchenSummary | null
  buddy: BuddyChoice | null
  onBuddy: (b: BuddyChoice) => void
}) {
  const [view, setView] = useState<View>('trophies')
  return (
    <>
      <div className="tabs">
        {([
          ['trophies', 'Trophies'], ['favorites', 'Favourites'], ['collections', 'Collections'],
          ['history', 'History'], ['categories', 'Categories'],
          ...(canTriage ? [['feedback', 'Feedback'] as [View, string]] : []),
        ] as [View, string][]).map(([key, label]) => (
          <button key={key} type="button" aria-current={view === key} onClick={() => setView(key)}>{label}</button>
        ))}
      </div>
      {view === 'trophies' && <Trophies summary={summary} buddy={buddy} onBuddy={onBuddy} />}
      {view === 'favorites' && <Favorites recipes={recipes} onChanged={onChanged} onOpen={onOpen} onCook={onCook} />}
      {view === 'collections' && <Collections recipes={recipes} onOpen={onOpen} />}
      {view === 'history' && <HistoryView onOpenRecipe={(id) => { const r = recipes.find((x) => x.id === id); if (r) onOpen(r) }} />}
      {view === 'categories' && <Categories categories={categories} onChanged={onChanged} />}
      {view === 'feedback' && canTriage && <Triage />}
    </>
  )
}

/**
 * Favourites — where cooked recipes stay VISIBLE.
 *
 * Discovery hides what she has made, because its job is finding something new.
 * This screen's job is the opposite, so it never hides anything, and the
 * grouping is by categories she already has rather than by a ranking the app
 * invented.
 */
function Favorites({ recipes, onChanged, onOpen, onCook }: {
  recipes: RecipeRow[]
  onChanged: () => void
  onOpen: (r: RecipeRow) => void
  onCook: (r: RecipeRow) => void
}) {
  const favorites = recipes.filter((r) => r.favorite)
  const favorite = async (r: RecipeRow) => { await updateRecipe(r.id, { favorite: !r.favorite }); onChanged() }

  if (!favorites.length) {
    return <Empty icon="bowl" title="No favourites yet"><p>Tap the heart on any recipe and it lands here — cooked or not.</p></Empty>
  }

  /* Grouped by the categories that actually exist on these recipes. No invented
     rankings, no scores — the only ordering the app knows is hers. */
  const groups = new Map<string, RecipeRow[]>()
  for (const r of favorites) {
    const keys = r.categories.filter((c) => c.kind === 'dish' || c.kind === 'area').map((c) => c.name)
    for (const key of keys.length ? keys : ['Everything else']) {
      groups.set(key, [...(groups.get(key) ?? []), r])
    }
  }

  return (
    <>
      <p className="muted small">{favorites.length} favourites. Cooked ones stay here.</p>
      {[...groups.entries()].sort((a, b) => b[1].length - a[1].length).map(([name, list]) => (
        <section key={name} style={{ marginBottom: 32 }}>
          <h2>{name} <span className="muted small">{list.length}</span></h2>
          <div className="deck">
            {list.map((r) => <RecipeCard key={r.id} recipe={r} onOpen={onOpen} onFavorite={favorite} />)}
          </div>
          <button type="button" style={{ marginTop: 12 }} onClick={() => onCook(list[Math.floor(Math.random() * list.length)]!)}>
            <Icon name="sparkle" size={15} /> Cook one of these
          </button>
        </section>
      ))}
    </>
  )
}

/** Ranked collections. The order is hers; the cap is enforced by the server. */
function Collections({ recipes, onOpen }: { recipes: RecipeRow[]; onOpen: (r: RecipeRow) => void }) {
  const [collections, setCollections] = useState<Collection[]>([])
  const [name, setName] = useState('')
  const [cap, setCap] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => setCollections(await listCollections()), [])
  useEffect(() => { void reload() }, [reload])

  return (
    <>
      <div className="card">
        <h2>A new collection</h2>
        <form
          className="filter-row"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!name.trim()) return
            await createCollection(name.trim(), undefined, cap ? Number(cap) : null)
            setName(''); setCap('')
            await reload()
          }}
        >
          <input className="grow" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mediterranean favourites" aria-label="Collection name" style={{ marginTop: 0 }} />
          <input type="number" inputMode="numeric" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="limit" aria-label="Limit" style={{ marginTop: 0, maxWidth: 100 }} />
          <button type="submit" className="primary" style={{ width: 'auto' }} disabled={!name.trim()}>Create</button>
        </form>
      </div>

      {error && <p className="error">{error}</p>}

      {!collections.length && <Empty icon="book" title="No collections yet"><p>A collection is a short, ordered list you keep by hand — a top five, the Mediterranean ones, the quick ones.</p></Empty>}

      {collections.map((c) => (
        <div className="card" key={c.id}>
          <div className="filter-row">
            <h2 style={{ margin: 0 }}>{c.name}</h2>
            <span className="muted small">{c.items.length}{c.cap ? ` of ${c.cap}` : ''}</span>
            <button
              type="button" className="ghost" style={{ marginLeft: 'auto' }}
              onClick={async () => { if (confirm(`Delete "${c.name}"?`)) { await deleteCollection(c.id); await reload() } }}
            >
              <Icon name="trash" size={15} />
            </button>
          </div>
          {c.description && <p className="muted small">{c.description}</p>}

          <ol className="list">
            {c.items.map((item) => (
              <li key={item.recipeId}>
                <span>
                  <b>{item.rank}.</b>{' '}
                  <button type="button" className="link" onClick={() => { const r = recipes.find((x) => x.id === item.recipeId); if (r) onOpen(r) }}>{item.title}</button>
                </span>
                <button type="button" onClick={async () => { await removeFromCollection(c.id, item.recipeId); await reload() }} aria-label={`Remove ${item.title}`}>
                  <Icon name="close" size={15} />
                </button>
              </li>
            ))}
          </ol>

          <select
            value="" aria-label={`Add to ${c.name}`}
            onChange={async (e) => {
              if (!e.target.value) return
              setError(null)
              try {
                await addToCollection(c.id, e.target.value)
                await reload()
              } catch (err) {
                /* The cap is a server rule, so this is the real answer, not a
                   guess made in the browser. */
                setError(err instanceof Error ? err.message : 'Could not add that.')
              }
            }}
          >
            <option value="">Add a recipe…</option>
            {recipes.filter((r) => !c.items.some((i) => i.recipeId === r.id)).map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
        </div>
      ))}
    </>
  )
}

function HistoryView({ onOpenRecipe }: { onOpenRecipe: (id: string) => void }) {
  const [runs, setRuns] = useState<Run[]>([])
  useEffect(() => { void history().then(setRuns) }, [])

  if (!runs.length) return <Empty icon="timer" title="Nothing cooked yet"><p>Finish a cooking session and it shows up here.</p></Empty>

  return (
    <>
      <p className="muted small">{runs.length} cooks recorded.</p>
      <ul className="list">
        {runs.map((run) => (
          <li key={run.id}>
            <div>
              <button type="button" className="link" onClick={() => onOpenRecipe(run.recipeId)}><b>{run.title}</b></button>
              <div className="muted small">
                {run.cookedOn}
                {run.source === 'manual' && ' · recorded by hand'}
                {run.servingsActual && ` · made ${run.servingsActual}`}
              </div>
              {run.notes && <div className="small">{run.notes}</div>}
              {!!run.tags.length && <div className="chips" style={{ marginTop: 6 }}>{run.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>}
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

function Categories({ categories, onChanged }: { categories: Category[]; onChanged: () => void }) {
  const [list, setList] = useState(categories)
  useEffect(() => setList(categories), [categories])

  return (
    <>
      <div className="card">
        <h2>Add a category</h2>
        <AddCategory onAdd={async (kind, name) => { await createCategory(kind, name); onChanged() }} />
      </div>

      {CATEGORY_KINDS.map(({ key, label }) => {
        const group = list.filter((c) => c.kind === key)
        if (!group.length) return null
        return (
          <div className="card" key={key}>
            <h2>{label}</h2>
            <ul className="list">
              {group.map((c) => (
                <li key={c.id}>
                  <input
                    defaultValue={c.name} aria-label={`Rename ${c.name}`}
                    style={{ marginTop: 0, maxWidth: 280 }}
                    onBlur={async (e) => {
                      if (e.target.value.trim() && e.target.value !== c.name) {
                        setList(await renameCategory(c.id, e.target.value.trim()))
                        onChanged()
                      }
                    }}
                  />
                  <span className="row">
                    <span className="muted small">{c.count} recipe{c.count === 1 ? '' : 's'}</span>
                    <button
                      type="button" aria-label={`Delete ${c.name}`}
                      onClick={async () => {
                        if (!confirm(`Delete "${c.name}"? It comes off ${c.count} recipe${c.count === 1 ? '' : 's'}.`)) return
                        setList(await deleteCategory(c.id))
                        onChanged()
                      }}
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </>
  )
}

export { CategoryChips }
